package at.theatervogelweide.admin.contract.imprint

import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.club.Club
import at.theatervogelweide.admin.contract.validate
import at.theatervogelweide.domain.Bic
import at.theatervogelweide.domain.Iban
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable

/**
 * The Impressum (#84, #147) as the public site shows it on `/impressum`: the club's address, ZVR-Zahl, contact and bank
 * details and "Weitere Angaben", which only superusers change, and the [obmann] from the board.
 */
@Serializable
data class Imprint(
    val version: Long,
    val draft: ImprintDraft,
    /** Who on the board has the function [Club.CONTACT_FUNCTION], whom the Impressum names as "Vertretung"; `null` for nobody. */
    val obmann: String?,
    /** UTC, as stored in the database. */
    val changedAt: LocalDateTime,
    /** The person of the account that saved it last, or its e-mail address; `null` for the migration's text or a deleted account. */
    val changedBy: String?,
) {
    val versionedId: VersionedId get() = VersionedId(ID, version)

    companion object {
        /** The one row there is. */
        const val ID = 1L
    }
}

/**
 * What a superuser saves (#147): every field is optional, empty for none. The [iban] and [bic] may be typed in any
 * case and with spaces; [normalized] stores them in capitals without spaces.
 */
@Serializable
data class ImprintDraft(
    val street: String = "",
    val postalCode: String = "",
    val locality: String = "",
    val zvrNumber: String = "",
    val contactEmail: String = "",
    val contactPhone: String = "",
    val bankAccountHolder: String = "",
    val iban: String = "",
    val bic: String = "",
    /** "Weitere Angaben" in Markdown. */
    val additionalText: String = "",
) {
    fun validate(): List<FieldError> {
        val draft = normalized()
        return validate {
            field(STREET, draft.street) { maxLength(MAX_STREET) }
            field(POSTAL_CODE, draft.postalCode) { maxLength(MAX_POSTAL_CODE) }
            field(LOCALITY, draft.locality) { maxLength(MAX_LOCALITY) }
            field(ZVR_NUMBER, draft.zvrNumber) { maxLength(MAX_ZVR_NUMBER) }
            field(CONTACT_EMAIL, draft.contactEmail) {
                email()
                maxLength(MAX_CONTACT_EMAIL)
            }
            field(CONTACT_PHONE, draft.contactPhone) {
                phone()
                maxLength(MAX_CONTACT_PHONE)
            }
            field(BANK_ACCOUNT_HOLDER, draft.bankAccountHolder) { maxLength(MAX_BANK_ACCOUNT_HOLDER) }
            field(IBAN, draft.iban) { iban() }
            field(BIC, draft.bic) { bic() }
            field(ADDITIONAL_TEXT, draft.additionalText) { maxLength(MAX_ADDITIONAL_TEXT) }
        }
    }

    /** As stored: single-line values without surrounding spaces, the IBAN and BIC in capitals without spaces. */
    fun normalized(): ImprintDraft = copy(
        street = street.trim(),
        postalCode = postalCode.trim(),
        locality = locality.trim(),
        zvrNumber = zvrNumber.trim(),
        contactEmail = contactEmail.trim(),
        contactPhone = contactPhone.trim(),
        bankAccountHolder = bankAccountHolder.trim(),
        iban = Iban.normalize(iban),
        bic = Bic.normalize(bic),
    )

    companion object {
        const val STREET = "street"
        const val POSTAL_CODE = "postalCode"
        const val LOCALITY = "locality"
        const val ZVR_NUMBER = "zvrNumber"
        const val CONTACT_EMAIL = "contactEmail"
        const val CONTACT_PHONE = "contactPhone"
        const val BANK_ACCOUNT_HOLDER = "bankAccountHolder"
        const val IBAN = "iban"
        const val BIC = "bic"
        const val ADDITIONAL_TEXT = "additionalText"

        const val MAX_STREET = 255
        const val MAX_POSTAL_CODE = 10
        const val MAX_LOCALITY = 255
        const val MAX_ZVR_NUMBER = 20
        const val MAX_CONTACT_EMAIL = 255
        const val MAX_CONTACT_PHONE = 50
        const val MAX_BANK_ACCOUNT_HOLDER = 255
        /** As much as the old single text held; the text generated for the old public site (#115) has room for the rest. */
        const val MAX_ADDITIONAL_TEXT = 10000
    }
}
